import React, { useRef, useState, useEffect } from "react";
import { ResponsiveContainer, Pie, PieChart, Cell } from "recharts";
import Alert from "react-bootstrap/Alert";
import Container from "../layout/Container";
import Panel from "../layout/Panel";
import FormContainer from "../layout/FormContainer";
import { Button, Col, Form, Row } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import GeographyPanel from "./GeographyPanel";
import HygieneChecksTable from "./HygieneTable";
import HygieneExampleTable from "./HygieneExampleTable";
import InsightTop10States from "./InsightTop10States";
import InsightTop10MSAs from "./InsightTop10MSAs";
import RevenueRange from "./RevenueRange";
import EmployeeRange from "./EmployeeRange";
import AddressRDI from "./AddressRDI";
import AgeOfBusiness from "./AgeOfBusiness";
import TopTrade from "./TopTrade";
import TopLicense from "./TopLicense";
import ServicePackedBubbleChart from "./ServicePackedBubbleChart";
import WordCloud from "./WordCloud";
import Input from "./Input";
import Enriched from "./Enriched";
import ProspectOpportunity from "./ProspectOpportunity";
import TopTradeType from "./TopTradeType";

const matchReportStyles = `
.match-report-page-title { font-weight: bold; font-size: 28px; }
.match-report-section-title { font-weight: bold; font-size: 20px; }
.match-report-stat-box {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 85px;
    border: 1px solid #d3d3d3;
    border-radius: 3px;
    padding: 8px 10px;
    text-align: center;
    background-color: #ffffff;
}
.match-report-stat-box--with-margin { margin-bottom: 10px; }
.match-report-stat-value { font-size: 30px; font-weight: bold; line-height: 1.1; }
.match-report-stat-label { margin-top: 2px; font-size: 16px; }
/* Summary box - inherits height from parent container */
.match-report-summary-box {
    width: 100%;
    text-align: center;
    border: 1px solid #d3d3d3;
    border-radius: 3px;
    padding: 12px;
    background-color: #ffffff;
    height: 100%;
    box-sizing: border-box;
}
.match-report-summary-chart { width: 100%; height: 200px; }
.match-report-chart-box {
    border: 1px solid #d3d3d3;
    border-radius: 3px;
    padding-top: 0px;
    padding-bottom: 0px;
    background-color: #ffffff;
}
.match-report-flex-end { display: flex; justify-content: flex-end; }
.match-report-logo { max-width: 100%; height: auto; max-height: 70px; }

/* Unified Quality Box */
.match-report-quality-unified-box {
    border: 1px solid #d3d3d3;
    border-radius: 3px;
    padding: 10px 10px 5px 10px;
    background-color: #ffffff;
    height: auto;
}

/* Top Row - Three Metrics */
.match-report-quality-metrics-row {
    display: flex;
    justify-content: space-around;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #d3d3d3;
}

.match-report-quality-metric {
    flex: 1;
    text-align: center;
    padding: 0 6px;
}

.match-report-quality-metric-value {
    font-size: 26px;
    font-weight: bold;
    line-height: 1.1;
    margin-bottom: 3px;
}

.match-report-quality-metric-label {
    font-size: 12px;
    color: #666;
    font-weight: 500;
}

/* Bar Chart Container */
.match-report-quality-bar-container {
    padding: 0;
    margin-top: 8px;
    min-height: 130px;
}

/* Section Container */
.match-report-section-container {
    margin-bottom: 0px;
}

.match-report-section-header {
    font-size: 20px;
    font-weight: bold;
    color: #2F2F33;
    margin-bottom: 12px;
    padding-bottom: 8px;
}
`;

export default function MatchReportDisplay({
    matchReport,
    matchReportError,
    onClearMatchReportError,
    reportSimpleMatchInfoRef,
    reportRow2Ref,
    reportRow3Ref,
    reportGeography,
    reportRow5Ref,
    reportRow6Ref,
    reportRow7Ref,
    reportRow8Ref,
    reportRow9Ref,
    handleDownloadPDF,
    downloading,
}) {
    const inputBoxRef = useRef(null);
    const [inputBoxHeight, setInputBoxHeight] = useState(null);

    useEffect(() => {
        if (!inputBoxRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                setInputBoxHeight(entry.contentRect.height);
            }
        });
        observer.observe(inputBoxRef.current);
        return () => observer.disconnect();
    }, []);

    function renameStatisticFields(dataArray) {
        return dataArray.map(item => {
            const match = item.statistic.match(/^cnt_(.*?)_match$/);
            let label = item.statistic;

            if (match && match[1]) {
                const titleCased = match[1]
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
                label = match[1] === 'no' ? titleCased + ' Match' : titleCased;
            }

            return { ...item, statistic: label };
        });
    }

    const statistics = matchReport?.matchStatistic ? renameStatisticFields(matchReport?.matchStatistic) : [];
    const recordCount = matchReport?.listRecordCount;
    const qualityRating = matchReport?.qualityRating;
    const matchRate = matchReport?.matchRate;
    const matchQualityAttribute = matchReport?.matchQualityAttribute;
    const sanitizedMatchQualityAttribute = (matchQualityAttribute ?? [])
        .map(item => ({
            ...item,
            cntValid: item.cntValid ?? 0,
            cntSuspect: item.cntSuspect ?? 0,
            cntInvalid: item.cntInvalid ?? 0,
        }));
    const hygieneCheck = matchReport?.matchQualityHygiene;
    const hygieneExample = matchReport?.matchQualityHygieneExample;
    const matchGeographyInformation = matchReport?.matchGeographyInformation;
    const insightTop10States = matchReport?.matchGeographyTop10States;
    const insightTop10MSAs = matchReport?.matchGeographyTop10MSAs;
    const revenueRanage = matchReport?.revenues;
    const employeeRange = matchReport?.employees;
    const addressRDI = matchReport?.addressRDI;
    const ageOfBusiness = matchReport?.ageOfBusiness;
    const topTrade = matchReport?.topTrades;
    const topLicense = matchReport?.topLicenses;
    const services = matchReport?.services;
    const licensedCount = matchReport?.licensed;
    const permittedCount = matchReport?.permitted;
    const affinityAndAffiliations = matchReport?.affinityAndAffiliations;
    const inputs = matchReport?.input;
    const enriched = matchReport?.enriched;
    const prospectOpp = matchReport?.prospectOpportunities;
    const topTradeTypes = matchReport?.topTradeTypes;
    const filteredData = statistics.filter(item => item.value > 0);
    const total = filteredData.reduce((sum, item) => sum + item.value, 0);
    const contractor = filteredData.find(item => item.statistic === "Contractor");
    const contractorMatchValue = contractor?.value || 0;
    const contractorMatchPercent = ((contractorMatchValue / total) * 100).toFixed(2);

    const totalRecords = recordCount?.count || 0;
    const validCount = qualityRating?.cntValid || 0;
    const suspectCount = qualityRating?.cntSuspect || 0;
    const invalidCount = qualityRating?.cntInvalid || 0;

    const validPercent = totalRecords > 0 ? ((validCount / totalRecords) * 100).toFixed(1) : 0;
    const suspectPercent = totalRecords > 0 ? ((suspectCount / totalRecords) * 100).toFixed(1) : 0;
    const invalidPercent = totalRecords > 0 ? ((invalidCount / totalRecords) * 100).toFixed(1) : 0;

    const categorizedCount = validCount + suspectCount + invalidCount;
    const undeterminedCount = Math.max(0, totalRecords - categorizedCount);

    return (
        <Container>
            <style dangerouslySetInnerHTML={{ __html: matchReportStyles }} />
            {matchReportError && (
                <Alert
                    variant="danger"
                    show={!!matchReportError}
                    onClose={onClearMatchReportError}
                    dismissible
                    transition={false}
                    className="mb-3"
                >
                    {matchReportError}
                </Alert>
            )}
            <Panel>
                <FormContainer>
                    <Form noValidate>
                        <Form.Group controlId="viewMatchReportForm">
                            <div ref={reportSimpleMatchInfoRef}>
                                <Row>
                                    <Col md={1}></Col>
                                    <Col md={5} className="align-self-start">
                                        <Row>
                                            <Col md={12} className="match-report-page-title">
                                                Enrichment Report
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>{matchReport?.listName}</Col>
                                        </Row>
                                    </Col>
                                    <Col md={5}>
                                        <div className="match-report-flex-end">
                                            <img
                                                src="/img/TBD-_horizontal.png"
                                                alt="Toolbelt"
                                                className="match-report-logo"
                                            />
                                        </div>
                                    </Col>
                                </Row>
                                <Row className="align-items-start" style={{marginBottom: "0px"}}>
                                    <Col md={1}></Col>
                                    <Col md={10}>
                                        <div className="match-report-section-container">
                                            <div className="match-report-section-header">Summary</div>
                                            <div style={{ display: "flex" }}>
                                                <Row className="align-items-stretch" style={{ flex: 1 }}>
                                                    <Col md={6}>
                                                    <div className="match-report-stat-box" style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", marginBottom: "20px" }}>
                                                        <div style={{ fontSize: "48px", fontWeight: "bold", color: "#2d5d7e" }}>
                                                            {totalRecords.toLocaleString()}
                                                        </div>
                                                        <div style={{ fontSize: "16px", color: "#666", marginTop: "-10px", marginBottom: "12px" }}>
                                                            list records
                                                        </div>
                                                    </div>

                                                    <div className="match-report-quality-unified-box" style={{ marginBottom: "20px" }}>
                                                        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center"}}>
                                                            <div>
                                                                <div style={{ fontSize: "48px", fontWeight: "bold", textAlign: "center", marginTop: "-15px"}}>
                                                                    <span style={{
                                                                        color: qualityRating?.qualityRating?.toLowerCase() === "bad" ? "#c13727" :
                                                                               qualityRating?.qualityRating?.toLowerCase() === "okay" ? "#fdba43" : "#6da06f",
                                                                        textTransform: "capitalize"
                                                                    }}>
                                                                        {qualityRating?.qualityRating}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div style={{textAlign:"center", fontSize: "12px", color: "#666", padding: "0 12px" }}>
                                                            {(() => {
                                                                const vp = totalRecords > 0 ? ((1 - (suspectCount + invalidCount) / totalRecords) * 100) : 0;
                                                                const ratingColor = vp > 90 ? "#6da06f" : vp > 50 ? "#fdba43" : "#c13727";
                                                                return (
                                                                    <>
                                                                        based on <span style={{ fontWeight: "bold", color: ratingColor }}>{vp.toFixed(1)}%</span> valid records versus <span style={{ fontWeight: "bold", color: "#939598" }}>{suspectCount.toLocaleString()}</span> suspect and <span style={{ fontWeight: "bold", color: "#939598" }}>{invalidCount.toLocaleString()}</span> invalid records.
                                                                    </>
                                                                );
                                                            })()}
                                                            </div>
                                                            <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%", padding: "0 10px 10px" }}>
                                                                <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: "2px", width: "100%" }}>
                                                                    {(() => {
                                                                        const vp = totalRecords > 0 ? ((1 - (suspectCount + invalidCount) / totalRecords) * 100) : 0;
                                                                        const ratingColor = vp > 90 ? "#6da06f" : vp > 50 ? "#fdba43" : "#c13727";
                                                                        return (
                                                                            <>
                                                                                <div style={{ display: "flex", flexDirection: "row", width: "100%", height: "30px", borderRadius: "4px", overflow: "hidden", border: "1px solid #d3d3d3", position: "relative" }}>
                                                                                    <div style={{ flex: 5, backgroundColor: "#fce8e6", display: "flex", alignItems: "center", justifyContent: "center", color: "#c13727", fontWeight: "600", fontSize: "11px", borderRight: "1px solid #d3d3d3" }}>Bad (0-50% valid)</div>
                                                                                    <div style={{ flex: 4, backgroundColor: "#fef5e7", display: "flex", alignItems: "center", justifyContent: "center", color: "#fdba43", fontWeight: "600", fontSize: "11px", borderRight: "1px solid #d3d3d3" }}>Okay (51-90% valid)</div>
                                                                                    <div style={{ flex: 1, backgroundColor: "#e8f5e9", display: "flex", alignItems: "center", justifyContent: "center", color: "#6da06f", fontWeight: "600", fontSize: "11px" }}>Good</div>
                                                                                    <div style={{ position: "absolute", left: "0", top: "0", width: `${vp}%`, height: "100%", background: "rgba(45, 53, 79, 0.3)", pointerEvents: "none", zIndex: 5 }}></div>
                                                                                    <div style={{ position: "absolute", left: `${vp}%`, top: "0", width: "3px", height: "30px", backgroundColor: ratingColor, zIndex: 10 }}></div>
                                                                                </div>
                                                                                <div style={{ position: "absolute", left: `${vp}%`, bottom: "-12px", transform: "translateX(-50%)", width: "0", height: "0", borderLeft: "8px solid transparent", borderRight: "8px solid transparent", borderBottom: `12px solid ${ratingColor}` }}></div>
                                                                                <div style={{ position: "absolute", left: `calc(${vp}% + 12px)`, bottom: "-15px", fontSize: "11px", fontWeight: "600", color: ratingColor, whiteSpace: "nowrap" }}>{vp.toFixed(1)}%</div>
                                                                            </>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="match-report-stat-box" style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                                                        <div style={{ fontSize: "48px", fontWeight: "bold", color: "#6da06f" }}>
                                                            {matchRate?.matchRate ? (matchRate.matchRate * 100).toFixed(1) : 0}%
                                                        </div>
                                                        <div style={{ fontSize: "16px", color: "#666", marginTop: "-10px", marginBottom: "12px" }}>
                                                            match rate
                                                        </div>
                                                    </div>
                                                    </Col>

                                                    <Col md={6} style={{ display: "flex", flexDirection: "column" }}>
                                                        <div className="match-report-summary-box">
                                                            <div style={{ padding: "0px 0px", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                                                                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                    <ResponsiveContainer width="100%" height="100%">
                                                                        <PieChart>
                                                                            <Pie
                                                                                data={filteredData}
                                                                                cx="50%"
                                                                                cy="50%"
                                                                                innerRadius="60%"
                                                                                outerRadius="92%"
                                                                                dataKey="value"
                                                                                nameKey="statistic"
                                                                                strokeWidth={2}
                                                                            >
                                                                                {filteredData.map((entry, index) => {
                                                                                    let color = "#8884d8";
                                                                                    let strokeColor = "#5a6d8d";
                                                                                    if (entry.statistic === "Contractor") { color = "#ee5a38"; strokeColor = "#b83f28"; }
                                                                                    else if (entry.statistic === "Business") { color = "#2d5d7e"; strokeColor = "#1a3a4d"; }
                                                                                    else if (entry.statistic === "No Match") { color = "#939598"; strokeColor = "#6a6d70"; }
                                                                                    return <Cell key={`cell-${index}`} fill={color} stroke={strokeColor} />;
                                                                                })}
                                                                            </Pie>
                                                                        </PieChart>
                                                                    </ResponsiveContainer>
                                                                </div>
                                                                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center", pointerEvents: "none" }}>
                                                                    {filteredData.map((entry, index) => {
                                                                        let color = "#333";
                                                                        if (entry.statistic === "Contractor") color = "#ee5a38";
                                                                        else if (entry.statistic === "Business") color = "#2d5d7e";
                                                                        else if (entry.statistic === "No Match") color = "#939598";
                                                                        const percent = (entry.value / filteredData.reduce((sum, d) => sum + d.value, 0) * 100).toFixed(1);
                                                                        return (
                                                                            <div key={index} style={{ marginBottom: index < filteredData.length - 1 ? "12px" : "0" }}>
                                                                                <div style={{ fontSize: "16px", color: "#666", marginBottom: "0px" }}>
                                                                                    <span style={{ fontWeight: "bold", color: color }}>{percent}%</span> <span style={{ fontSize: "13px" }}>({entry.value.toLocaleString()})</span>
                                                                                </div>
                                                                                <div style={{ fontSize: "16px", fontWeight: "700", color: color, marginTop:"-5px" }}>
                                                                                    {entry.statistic}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                            <div ref={reportRow2Ref}>
                                <Row>
                                    <Col md={1}></Col>
                                    <Col md={10}>
                                        <div className="match-report-section-container">
                                            <div className="match-report-section-header">Quality</div>
                                            <div style={{ display: "flex" }}>
                                                <Row className="align-items-stretch" style={{ flex: 1 }}>
                                                <Col md={5}>
                                                    <div className="match-report-chart-box">
                                                        <div style={{ fontSize: "16px", fontWeight: "700", padding: "6px 10px", borderBottom: "1px solid #d3d3d3", background: "#f5f5f5" }}>
                                                            Attribute Quality
                                                        </div>
                                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #d3d3d3", fontWeight: "600" }}></th>
                                                                    <th style={{ textAlign: "right", padding: "8px", borderBottom: "1px solid #d3d3d3", backgroundColor: "#e8f5e9", fontWeight: "600", color: "#2e7d32" }}>Valid</th>
                                                                    <th style={{ textAlign: "right", padding: "8px", borderBottom: "1px solid #d3d3d3", backgroundColor: "#fff4e6", fontWeight: "600", color: "#e65100" }}>Suspect</th>
                                                                    <th style={{ textAlign: "right", padding: "8px", borderBottom: "1px solid #d3d3d3", backgroundColor: "#ffe6e6", fontWeight: "600", color: "#c62828" }}>Invalid</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {sanitizedMatchQualityAttribute.map((item, index) => (
                                                                    <tr key={index}>
                                                                        <td style={{ padding: "6px 8px", borderBottom: "1px solid #e0e0e0", fontSize: "11px" }}>
                                                                            {item.attributeType.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ")}
                                                                        </td>
                                                                        <td style={{ padding: "6px 8px", borderBottom: "1px solid #e0e0e0", textAlign: "right", backgroundColor: "#e8f5e9", fontWeight: "600" }}>{item.cntValid?.toLocaleString()}</td>
                                                                        <td style={{ padding: "6px 8px", borderBottom: "1px solid #e0e0e0", textAlign: "right", backgroundColor: "#fff4e6", fontWeight: "600" }}>{item.cntSuspect?.toLocaleString()}</td>
                                                                        <td style={{ padding: "6px 8px", borderBottom: "1px solid #e0e0e0", textAlign: "right", backgroundColor: "#ffe6e6", fontWeight: "600" }}>{item.cntInvalid?.toLocaleString()}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </Col>
                                                <Col md={7} className="hygiene-checks-col" style={{ display: "flex", flexDirection: "column" }}>
                                                    <HygieneChecksTable data={hygieneCheck}/>
                                                </Col>
                                                </Row>
                                            </div>
                                            <Row className="align-items-start" style={{ marginTop: "10px" }}>
                                                <Col md={12}>
                                                    <HygieneExampleTable data={hygieneExample} />
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                            <div ref={reportRow3Ref} style={{ display: "none" }}>
                                {/* Kept for PDF generation */}
                            </div>
                            <div ref={reportGeography}>
                                <Row>
                                    <Col md={1}></Col>
                                    <Col md={10}>
                                        <div className="match-report-section-container">
                                            <div className="match-report-section-header">Insight</div>
                                            <Row className="align-items-start">
                                                <Col md={6} style={{ flexShrink: 0 }}>
                                                    <div style={{ height: "350px", minHeight: "350px", maxHeight: "350px", width: "100%", overflow: "hidden", position: "relative", flexShrink: 0 }}>
                                                        <GeographyPanel targetCounties={matchGeographyInformation} />
                                                    </div>
                                                </Col>
                                                <Col md={2}>
                                                    <InsightTop10States data={insightTop10States} />
                                                </Col>
                                                <Col md={4}>
                                                    <InsightTop10MSAs data={insightTop10MSAs} />
                                                </Col>
                                            </Row>
                                            <Row className="align-items-start" style={{ marginTop: "10px" }}>
                                                <Col md={6}><RevenueRange data={revenueRanage} /></Col>
                                                <Col md={6}><EmployeeRange data={employeeRange} /></Col>
                                            </Row>
                                            <Row className="align-items-start" style={{ marginTop: "10px" }}>
                                                <Col md={6}><AddressRDI data={addressRDI} /></Col>
                                                <Col md={6}><AgeOfBusiness data={ageOfBusiness} /></Col>
                                            </Row>
                                            <Row className="align-items-start" style={{ marginTop: "10px" }}>
                                                <Col md={6}><TopTrade data={topTrade} /></Col>
                                                <Col md={6}><TopLicense data={topLicense} /></Col>
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                            <div ref={reportRow5Ref} style={{ display: "none" }}>
                                {/* Kept for PDF generation */}
                            </div>
                            <div ref={reportRow6Ref}>
                                <Row>
                                    <Col md={1}></Col>
                                    <Col md={10}>
                                        <div className="match-report-section-container">
                                            <Row className="align-items-start">
                                                <Col md={12}>
                                                    <div style={{ border: "1px solid #d3d3d3", borderRadius: "3px", background: "#fff", display: "flex", flexDirection: "column" }}>
                                                        <div className="hc-title">Service</div>
                                                        <div style={{ paddingTop: "10px", paddingLeft: "10px", paddingRight: "10px", height: "650px", display: "flex", flexDirection: "column", minHeight: 0 }}>
                                                            <ServicePackedBubbleChart data={services} width={950} height={700} topN={60} />
                                                        </div>
                                                        <div style={{ display: "flex", flexDirection: "row", gap: "10px", padding: "10px" }}>
                                                            <div className="match-report-stat-box" style={{ flex: 1 }}>
                                                                <div className="match-report-stat-value" style={{ color: "#2d5d7e" }}>{licensedCount && licensedCount[0]?.count?.toLocaleString()}</div>
                                                                <div className="match-report-stat-label">Licensed</div>
                                                            </div>
                                                            <div className="match-report-stat-box" style={{ flex: 1 }}>
                                                                <div className="match-report-stat-value" style={{ color: "#2d5d7e" }}>{permittedCount && permittedCount[0]?.count?.toLocaleString()}</div>
                                                                <div className="match-report-stat-label">Permitted</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                            <div ref={reportRow7Ref}>
                                <Row>
                                    <Col md={1}></Col>
                                    <Col md={10}>
                                        <div className="match-report-section-container">
                                            <Row className="align-items-start">
                                                <Col md={12}>
                                                    <div style={{ border: "1px solid #d3d3d3", borderRadius: "3px", background: "#fff", display: "flex", flexDirection: "column" }}>
                                                        <div className="hc-title">Affinity and Affiliation</div>
                                                        <div style={{ padding: "20px 10px" }}>
                                                            <WordCloud
                                                                data={affinityAndAffiliations}
                                                                width={1600}
                                                                height={Math.max(200, Math.min(400, 200 + (affinityAndAffiliations?.length || 0) * 1))}
                                                                topN={5000}
                                                            />
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                            <div ref={reportRow8Ref}>
                                <Row>
                                    <Col md={1}></Col>
                                    <Col md={10}>
                                        <div className="match-report-section-container">
                                            <div className="hc-box" style={{ height: "auto", overflow: "visible", display: "flex", flexDirection: "column" }}>
                                                <div className="hc-title">Example Enrichment</div>
                                                <div style={{ display: "flex", gap: "10px", padding: "10px" }}>
                                                    <div style={{ flex: "0 0 35%", display: "flex", flexDirection: "column", minHeight: 0 }}>
                                                        <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "8px", paddingLeft: "4px" }}>Input</div>
                                                        <div ref={inputBoxRef} style={{ border: "1px solid #e0e0e0", borderRadius: "3px", backgroundColor: "#fafafa" }}>
                                                            <Input data={inputs} grayed={true} />
                                                        </div>
                                                    </div>
                                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "30px" }}>
                                                        <div style={{ width: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#2d5d7e", display: "flex", alignItems: "center", justifyContent: "center", color: "#ffffff", fontSize: "20px", fontWeight: "bold", boxShadow: "0 2px 4px rgba(0,0,0,0.2)" }}>
                                                            <FontAwesomeIcon icon={faChevronRight} />
                                                        </div>
                                                    </div>
                                                    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                                                        <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "8px", paddingLeft: "4px" }}>Enriched</div>
                                                        <div style={{ border: "1px solid #e0e0e0", borderRadius: "3px", backgroundColor: "#fafafa", ...(inputBoxHeight != null && { height: inputBoxHeight, overflowY: "auto" }) }}>
                                                            <Enriched data={enriched} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                            <div ref={reportRow9Ref}>
                                <Row>
                                    <Col md={1}></Col>
                                    <Col md={10}>
                                        <div className="match-report-section-container">
                                            <div style={{ border: "1px solid #d3d3d3", borderRadius: "3px", background: "#fff", display: "flex", flexDirection: "column" }}>
                                                <div className="hc-title">Prospect Opportunity</div>
                                                <div style={{ padding: "10px" }}>
                                                    <Row className="align-items-start">
                                                        <Col md={12}>
                                                            <ProspectOpportunity regionData={prospectOpp} />
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </div>
                                            <Row className="align-items-start" style={{ marginTop: "20px" }}>
                                                <Col md={12}>
                                                    <TopTradeType data={topTradeTypes} />
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                            <div>
                                <Row>
                                    <Col xl={1} lg={1} md={1} sm={1}></Col>
                                    <Col xl={6} lg={6} md={6} sm={6} className="buttons">
                                        {downloading && (
                                            <Button className="btn-min-width-120 btn-height-35" variant="info" onClick={handleDownloadPDF}>
                                                <Spinner style={{marginRight: '5px', verticalAlign: 'baseline'}} as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                Downloading..
                                            </Button>
                                        )}
                                        {!downloading && (
                                            <Button className="btn-min-width-120 btn-height-35" variant="info" onClick={handleDownloadPDF}>
                                                Download as PDF
                                            </Button>
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        </Form.Group>
                    </Form>
                </FormContainer>
            </Panel>
        </Container>
    );
}
